<script setup>
import { computed, inject, onMounted, reactive, ref } from 'vue'
import { MousePointerClick, RefreshCw, Search } from '@lucide/vue'
import { Badge, Button, EmptyState, Input, PageHeader, Section, SegmentedControl, Spinner, Stat, Toolbar } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { paidActionRefundReason } from '@drama/contracts/platform'
import PointsFlowChart from '../../components/billing/PointsFlowChart.vue'
import ProductSalesChart from '../../components/billing/ProductSalesChart.vue'
import RevenueTrendChart from '../../components/billing/RevenueTrendChart.vue'
import SpendMixChart from '../../components/billing/SpendMixChart.vue'
import { formatInt, formatRate } from '../llm/llm-format.js'
import { formatCurrency } from './format.js'

const QUICK_RANGES = [7, 30, 90]
const CUSTOM_RANGE = 'custom'

const api = inject('api')
if (!api) throw new Error('BillingDashboard: api provider is required')
const { t } = useI18n()
const router = useRouter()

const range = reactive({ mode: '30', since: '', until: '' })
const report = ref(null)
const loading = ref(false)
const error = ref('')

const rangeOptions = computed(() => [
  ...QUICK_RANGES.map(days => ({ value: String(days), label: t('visualGameAdmin.billing.dashboard.range.days', { n: days }) })),
  { value: CUSTOM_RANGE, label: t('visualGameAdmin.billing.dashboard.range.custom') },
])

onMounted(load)

function queryFor() {
  return range.mode === CUSTOM_RANGE ? { since: range.since, until: range.until } : { days: Number(range.mode) }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    report.value = await api.commerce.adminDashboard(queryFor())
    range.since = report.value.since
    range.until = report.value.until
  } catch (cause) {
    error.value = cause.message
  } finally {
    loading.value = false
  }
}

function changeRange(mode) {
  range.mode = mode
  if (mode !== CUSTOM_RANGE) load()
}

function money(cents) {
  return formatCurrency(cents, 'usd')
}

function rate(value) {
  return value === null ? '—' : formatRate(value)
}

/**
 * 下钻全是深链到订单页 / 流水页，带上当前时间窗（或点中的那一天）和对应的筛选条件。
 * 时间窗用接口给的 start_at / end_at：那是报表时区的自然日边界，前端不自己算时区。
 */
function ordersLink(filters, bounds = report.value) {
  return { path: '/billing/orders', query: { since: bounds.start_at, until: bounds.end_at, ...filters } }
}

function ledgerLink(filters, bounds = report.value) {
  return { path: '/billing/ledger', query: { since: bounds.start_at, until: bounds.end_at, ...filters } }
}

function openSpend(item) {
  router.push(ledgerLink(item.ref_type === null ? { reason: item.reason } : { reason: item.reason, ref_type: item.ref_type }))
}

/** 指标卡分组：每组 [key, 标签, 已格式化的值, tone, 下钻链接或 null]，模板只管铺。 */
function section(name) {
  return key => t(`visualGameAdmin.billing.dashboard.${name}.${key}`)
}

function actionName(action) {
  return t(`visualGameAdmin.billing.actionNames.${action}`)
}

const revenueStats = computed(() => {
  const revenue = report.value.revenue
  const label = section('revenue')
  return [
    ['gross', label('gross'), money(revenue.gross_cents), 'neutral', ordersLink({ status: 'paid' })],
    ['refund', label('refund'), money(revenue.refund_cents), revenue.refund_cents > 0 ? 'warning' : 'neutral', ordersLink({ status: 'refunded' })],
    ['net', label('net'), money(revenue.net_cents), 'success', null],
    ['paidOrders', label('paidOrders'), formatInt(revenue.paid_orders), 'neutral', ordersLink({ status: 'paid' })],
    ['payingUsers', label('payingUsers'), formatInt(revenue.paying_users), 'neutral', ordersLink({ status: 'paid' })],
    ['firstTimeBuyers', label('firstTimeBuyers'), formatInt(revenue.first_time_buyers), 'neutral', null],
  ]
})
// 付费点的消耗、退还卡片按接口给的付费点列表生成。
const pointStats = computed(() => {
  const points = report.value.points
  const paidActions = report.value.paid_actions
  const label = section('points')
  return [
    ['purchasedPaid', label('purchasedPaid'), formatInt(points.purchased_paid), 'neutral', ledgerLink({ reason: 'purchase_paid' })],
    ['purchasedBonus', label('purchasedBonus'), formatInt(points.purchased_bonus), 'neutral', ledgerLink({ reason: 'purchase_bonus' })],
    ['dailyClaim', label('dailyClaim'), formatInt(points.daily_claim), 'neutral', ledgerLink({ reason: 'daily_claim' })],
    ['signupReward', label('signupReward'), formatInt(points.signup_reward), 'neutral', ledgerLink({ reason: 'signup_gift' })],
    ['adminGrant', label('adminGrant'), formatInt(points.admin_grant), 'neutral', ledgerLink({ reason: 'admin_adjustment' })],
    ...paidActions.map(item => [
      `spent:${item.action}`,
      t('visualGameAdmin.billing.dashboard.points.actionSpent', { name: actionName(item.action) }),
      formatInt(item.spent),
      'neutral',
      ledgerLink({ reason: item.action }),
    ]),
    ['otherSpent', label('otherSpent'), formatInt(points.other_spent), 'neutral', ledgerLink({})],
    ...paidActions.map(item => [
      `refunded:${item.action}`,
      t('visualGameAdmin.billing.dashboard.points.actionRefunded', { name: actionName(item.action) }),
      formatInt(item.refunded),
      item.refunded > 0 ? 'warning' : 'neutral',
      ledgerLink({ reason: paidActionRefundReason(item.action) }),
    ]),
  ]
})
const conversionStats = computed(() => {
  const conversion = report.value.conversion
  const label = section('conversion')
  return [
    ['newUsers', label('newUsers'), formatInt(conversion.new_users), 'neutral', null],
    ['newUsersPaid', label('newUsersPaid'), formatInt(conversion.new_users_paid), 'neutral', null],
    ['signupToPaidRate', label('signupToPaidRate'), rate(conversion.signup_to_paid_rate), 'success', null],
    ['activeUsers', label('activeUsers'), formatInt(conversion.active_users), 'neutral', ledgerLink({})],
    ['claimUsers', label('claimUsers'), formatInt(conversion.claim_users), 'neutral', ledgerLink({ reason: 'daily_claim' })],
    ['claimRate', label('claimRate'), rate(conversion.claim_rate), 'neutral', null],
  ]
})
const riskStats = computed(() => {
  const risk = report.value.risk
  const label = section('risk')
  return [
    ['refundedOrders', label('refundedOrders'), formatInt(risk.refunded_orders), risk.refunded_orders > 0 ? 'warning' : 'neutral', ordersLink({ status: 'refunded' })],
    ['refundRate', label('refundRate'), rate(risk.refund_rate), 'neutral', null],
    ['disputedOrders', label('disputedOrders'), formatInt(risk.disputed_orders), risk.disputed_orders > 0 ? 'danger' : 'neutral', ordersLink({ status: 'disputed' })],
    ['disputeRate', label('disputeRate'), rate(risk.dispute_rate), 'neutral', null],
    ...report.value.paid_actions.map(item => [
      `refunds:${item.action}`,
      t('visualGameAdmin.billing.dashboard.risk.actionRefunds', { name: actionName(item.action) }),
      formatInt(item.refunds),
      item.refunds > 0 ? 'warning' : 'neutral',
      ledgerLink({ reason: paidActionRefundReason(item.action) }),
    ]),
    ['shortfallOrders', label('shortfallOrders'), formatInt(risk.shortfall_orders), risk.shortfall_orders > 0 ? 'danger' : 'neutral', ordersLink({ shortfall_only: 'true' })],
    ['shortfallPoints', label('shortfallPoints'), formatInt(risk.shortfall_points), risk.shortfall_points > 0 ? 'danger' : 'neutral', ordersLink({ shortfall_only: 'true' })],
  ]
})
</script>

<template>
  <div class="vga-page" data-insp-path="apps/visual-game-admin/src/pages/billing/DashboardPage.vue">
    <PageHeader :title="t('visualGameAdmin.billing.dashboard.title')">
      <template #actions>
        <template v-if="report !== null">
          <span class="dashboard-window vga-mono">
            {{ report.since }} → {{ report.until }} · {{ t('visualGameAdmin.billing.dashboard.windowDays', { n: report.daily.length }) }}
          </span>
          <Badge variant="neutral">{{ report.timezone }}</Badge>
        </template>
        <Button variant="secondary" size="sm" :loading="loading" @click="load">
          <RefreshCw :size="14" />{{ t('visualGameAdmin.common.refresh') }}
        </Button>
      </template>
    </PageHeader>

    <Section>
      <Toolbar>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.billing.dashboard.range.label') }}</label>
          <SegmentedControl :model-value="range.mode" :options="rangeOptions" size="sm" @update:model-value="changeRange" />
        </div>
        <template v-if="range.mode === CUSTOM_RANGE">
          <div class="drama-toolbar-field">
            <label>{{ t('visualGameAdmin.billing.dashboard.range.since') }}</label>
            <Input v-model="range.since" type="date" size="sm" />
          </div>
          <div class="drama-toolbar-field">
            <label>{{ t('visualGameAdmin.billing.dashboard.range.until') }}</label>
            <Input v-model="range.until" type="date" size="sm" />
          </div>
        </template>
        <template v-if="range.mode === CUSTOM_RANGE" #actions>
          <Button size="sm" :loading="loading" @click="load">
            <Search :size="14" />{{ t('visualGameAdmin.waitlist.filters.search') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <div v-if="loading && report === null" class="vga-state"><Spinner /></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <template v-else-if="report !== null">
      <Section :title="t('visualGameAdmin.billing.dashboard.sections.revenue')">
        <template #actions>
          <span class="dashboard-drill"><MousePointerClick :size="12" />{{ t('visualGameAdmin.billing.dashboard.drill') }}</span>
        </template>
        <div class="dashboard-stats">
          <template v-for="[key, label, value, tone, to] in revenueStats" :key="key">
            <RouterLink v-if="to !== null" class="dashboard-stat-link" :to="to">
              <Stat :label="label" :value="value" :tone="tone" />
            </RouterLink>
            <Stat v-else :label="label" :value="value" :tone="tone" />
          </template>
        </div>
        <div class="dashboard-chart"><RevenueTrendChart :items="report.daily" @select="router.push(ordersLink({}, $event))" /></div>
      </Section>

      <Section :title="t('visualGameAdmin.billing.dashboard.sections.points')">
        <template #actions>
          <span class="dashboard-drill"><MousePointerClick :size="12" />{{ t('visualGameAdmin.billing.dashboard.drill') }}</span>
        </template>
        <div class="dashboard-stats">
          <template v-for="[key, label, value, tone, to] in pointStats" :key="key">
            <RouterLink v-if="to !== null" class="dashboard-stat-link" :to="to">
              <Stat :label="label" :value="value" :tone="tone" />
            </RouterLink>
            <Stat v-else :label="label" :value="value" :tone="tone" />
          </template>
        </div>
        <div class="dashboard-chart"><PointsFlowChart :items="report.daily" @select="router.push(ledgerLink({}, $event))" /></div>
      </Section>

      <div class="dashboard-columns">
        <Section :title="t('visualGameAdmin.billing.dashboard.sections.conversion')">
          <template #actions>
            <span class="dashboard-drill"><MousePointerClick :size="12" />{{ t('visualGameAdmin.billing.dashboard.drill') }}</span>
          </template>
          <div class="dashboard-stats dashboard-stats--narrow">
            <template v-for="[key, label, value, tone, to] in conversionStats" :key="key">
              <RouterLink v-if="to !== null" class="dashboard-stat-link" :to="to">
                <Stat :label="label" :value="value" :tone="tone" />
              </RouterLink>
              <Stat v-else :label="label" :value="value" :tone="tone" />
            </template>
          </div>
          <p class="dashboard-note">{{ t('visualGameAdmin.billing.dashboard.conversion.note') }}</p>
        </Section>
        <Section :title="t('visualGameAdmin.billing.dashboard.sections.risk')">
          <template #actions>
            <span class="dashboard-drill"><MousePointerClick :size="12" />{{ t('visualGameAdmin.billing.dashboard.drill') }}</span>
          </template>
          <div class="dashboard-stats dashboard-stats--narrow">
            <template v-for="[key, label, value, tone, to] in riskStats" :key="key">
              <RouterLink v-if="to !== null" class="dashboard-stat-link" :to="to">
                <Stat :label="label" :value="value" :tone="tone" />
              </RouterLink>
              <Stat v-else :label="label" :value="value" :tone="tone" />
            </template>
          </div>
          <p class="dashboard-note">{{ t('visualGameAdmin.billing.dashboard.risk.note') }}</p>
        </Section>
      </div>

      <div class="dashboard-columns">
        <Section :title="t('visualGameAdmin.billing.dashboard.sections.products')">
          <template #actions>
            <span class="dashboard-drill"><MousePointerClick :size="12" />{{ t('visualGameAdmin.billing.dashboard.drill') }}</span>
          </template>
          <EmptyState v-if="report.products.length === 0" :title="t('visualGameAdmin.billing.dashboard.emptyProducts')" />
          <div v-else class="dashboard-chart"><ProductSalesChart :items="report.products" @select="router.push(ordersLink({ product_id: $event.product_id, status: 'paid' }))" /></div>
        </Section>
        <Section :title="t('visualGameAdmin.billing.dashboard.sections.spendMix')">
          <template #actions>
            <span class="dashboard-drill"><MousePointerClick :size="12" />{{ t('visualGameAdmin.billing.dashboard.drill') }}</span>
          </template>
          <EmptyState v-if="report.spend_mix.length === 0" :title="t('visualGameAdmin.billing.dashboard.emptySpend')" />
          <div v-else class="dashboard-chart"><SpendMixChart :items="report.spend_mix" @select="openSpend" /></div>
        </Section>
      </div>
    </template>
  </div>
</template>

<style scoped>
.dashboard-window {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.dashboard-drill {
  display: inline-flex;
  align-items: center;
  gap: var(--drama-space-xs);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  font-weight: var(--drama-font-weight-normal);
}

.dashboard-chart {
  cursor: pointer;
}

.dashboard-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: var(--drama-space-md);
  margin-bottom: var(--drama-space-lg);
}

.dashboard-stats--narrow {
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
}

.dashboard-stat-link {
  display: block;
  border: 1px solid transparent;
  border-radius: var(--drama-radius-md);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.dashboard-stat-link:hover {
  border-color: var(--drama-color-primary);
  box-shadow: var(--drama-shadow-sm);
}

.dashboard-stat-link :deep(.drama-stat) {
  height: 100%;
  box-sizing: border-box;
}

.dashboard-columns {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  gap: var(--drama-space-xl);
  align-items: start;
}

.dashboard-note {
  margin: 0;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}
</style>
